import { useEffect, useMemo, useRef, useState } from 'react';
import {
    IconCopy,
    IconPlus,
    IconRotateClockwise,
    IconTrash,
} from '@tabler/icons-react';
import { useTranslation } from '../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../utils/bnFormat';
import { usePdfEditorStore } from '../state/usePdfEditorStore';
import { useCompactLayout } from '../ui/prims';
import Hint from '../../../components/common/Hint';
import { useThumbnails } from './useThumbnails';
import { paintObjects } from '../engine/drawObjects';

/**
 * A page thumbnail that shows the REAL page: the base raster (a real PDF's page, when
 * there is one) with the overlay objects painted on top — so a document made entirely of
 * text objects on a blank page is no longer an empty tile. Repaints whenever the page's
 * objects change, so thumbnails track edits live.
 */
function ThumbCanvas({ page, baseSrc, objects, rotation }) {
    const ref = useRef(null);
    useEffect(() => {
        const canvas = ref.current;
        if (!canvas) return undefined;
        const cssW = canvas.clientWidth || 150;
        const cssH = canvas.clientHeight || Math.round((cssW * page.height) / page.width);
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        canvas.width = Math.max(1, Math.round(cssW * dpr));
        canvas.height = Math.max(1, Math.round(cssH * dpr));
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        const scale = canvas.width / page.width; // device px per PDF point
        const paint = () => { try { paintObjects(ctx, objects || [], scale); } catch { /* ignore */ } };
        let cancelled = false;
        if (baseSrc) {
            const img = new Image();
            img.onload = () => { if (!cancelled) { ctx.drawImage(img, 0, 0, canvas.width, canvas.height); paint(); } };
            img.onerror = () => { if (!cancelled) paint(); };
            img.src = baseSrc;
        } else {
            paint();
        }
        return () => { cancelled = true; };
    }, [baseSrc, objects, page.width, page.height]);
    return <canvas ref={ref} className="h-full w-full" style={{ transform: `rotate(${rotation}deg)` }} />;
}

/**
 * Left page strip: jump to a page, drag to reorder, and the per-page quick
 * actions (rotate / duplicate / delete) that mirror the Manage-pages dialog.
 *
 * Two ways to add a page, because they add in different places: the `+` in the
 * header puts one after the page you are on, and the button at the foot of the
 * list puts one at the end.
 */
export default function ThumbnailsPanel() {
    const { t } = useTranslation();
    const pages = usePdfEditorStore((s) => s.pages);
    const pdf = usePdfEditorStore((s) => s.pdf);
    const currentPage = usePdfEditorStore((s) => s.currentPage);
    const objectsByUid = usePdfEditorStore((s) => s.objectsByUid);
    const goToPage = usePdfEditorStore((s) => s.goToPage);
    const rotatePage = usePdfEditorStore((s) => s.rotatePage);
    const deletePages = usePdfEditorStore((s) => s.deletePages);
    const duplicatePages = usePdfEditorStore((s) => s.duplicatePages);
    const insertBlankPage = usePdfEditorStore((s) => s.insertBlankPage);
    const movePage = usePdfEditorStore((s) => s.movePage);

    const compact = useCompactLayout();
    const [dragIndex, setDragIndex] = useState(null);
    const [overIndex, setOverIndex] = useState(null);

    const sourceIndices = useMemo(
        () => pages.map((p) => p.sourceIndex).filter((i) => i != null),
        [pages],
    );
    const thumbs = useThumbnails(pdf, sourceIndices);

    return (
        <aside className={`flex flex-col border-r border-[#ECE8E4] bg-white dark:border-zinc-800 dark:bg-zinc-900 ${
            // On a phone the strip floats over the page instead of taking a
            // fifth of a 390px-wide screen away from it.
            compact
                ? 'absolute inset-y-0 left-0 z-30 w-[142px] shadow-[8px_0_24px_rgba(28,24,21,0.18)]'
                : 'w-[176px] flex-none'
        }`}>
            <div className="flex h-[34px] flex-none items-center justify-between gap-2 border-b border-[#F0ECE8] px-[11px] dark:border-zinc-800">
                <span className="text-[12px] font-bold text-[#1C1815] dark:text-zinc-100">
                    {t('pdfEditor.ribbon.tabs.home')}
                </span>
                <div className="flex items-center gap-1">
                    <span className="text-[11px] text-[#8A827B] dark:text-zinc-500">
                        {t('pdfEditor.pagesCount', { count: toBnDigits(pages.length) })}
                    </span>
                    <Hint label={t('pdfEditor.pages.insertBlank')}>
                        <button
                            type="button"
                            onClick={() => insertBlankPage(currentPage)}
                            aria-label={t('pdfEditor.pages.insertBlank')}
                            className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-md text-[#6B635C] transition-colors hover:bg-[#F4F1ED] hover:text-[#1C1815] dark:text-zinc-400 dark:hover:bg-zinc-800"
                        >
                            <IconPlus size={14} stroke={2} />
                        </button>
                    </Hint>
                </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto p-[11px] [scrollbar-width:thin]">
                {pages.map((page, index) => {
                    const active = index === currentPage;
                    const count = (objectsByUid[page.uid] || []).length;
                    const src = page.sourceIndex != null ? thumbs[page.sourceIndex] : null;
                    const rotatedThumb = page.userRotation % 180 === 90;

                    return (
                        <div
                            key={page.uid}
                            draggable
                            onDragStart={() => setDragIndex(index)}
                            onDragOver={(e) => { e.preventDefault(); setOverIndex(index); }}
                            onDragEnd={() => { setDragIndex(null); setOverIndex(null); }}
                            onDrop={(e) => {
                                e.preventDefault();
                                if (dragIndex != null && dragIndex !== index) movePage(dragIndex, index);
                                setDragIndex(null);
                                setOverIndex(null);
                            }}
                            className={`group relative flex flex-none flex-col items-center gap-[5px] rounded-lg transition-colors ${
                                overIndex === index && dragIndex !== index ? 'bg-primary-50' : ''
                            }`}
                        >
                            <button
                                type="button"
                                onClick={() => goToPage(index)}
                                className={`block w-full overflow-hidden rounded bg-white transition-colors ${
                                    active
                                        ? 'ring-2 ring-primary'
                                        : 'ring-1 ring-[#E2DCD6] hover:ring-primary/50 dark:ring-zinc-700'
                                }`}
                                style={{ aspectRatio: rotatedThumb ? `${page.height} / ${page.width}` : `${page.width} / ${page.height}` }}
                            >
                                <ThumbCanvas
                                    page={page}
                                    baseSrc={src}
                                    // An open, unchanged paragraph is not an edit yet.
                                    objects={objectsByUid[page.uid]?.some((o) => o.pending)
                                        ? objectsByUid[page.uid].filter((o) => !o.pending)
                                        : objectsByUid[page.uid]}
                                    rotation={page.userRotation}
                                />
                            </button>

                            <div className="flex items-center gap-1">
                                <span className={`text-[11px] font-semibold ${
                                    active ? 'text-primary' : 'text-[#B8B1AA] dark:text-zinc-500'
                                }`}
                                >
                                    {toBnDigits(index + 1)}
                                </span>
                                {count ? (
                                    <span className="rounded-full bg-primary-50 px-1.5 text-[10px] font-semibold text-primary-800 dark:bg-primary-900/30 dark:text-primary-300">
                                        {toBnDigits(count)}
                                    </span>
                                ) : null}
                            </div>

                            <div className="pointer-events-none absolute right-1 top-1 flex gap-1 opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100">
                                <ThumbAction
                                    icon={IconRotateClockwise}
                                    label={t('pdfEditor.pages.rotate')}
                                    onClick={() => rotatePage(page.uid, 90)}
                                />
                                <ThumbAction
                                    icon={IconCopy}
                                    label={t('pdfEditor.pages.duplicate')}
                                    onClick={() => duplicatePages([page.uid])}
                                />
                                <ThumbAction
                                    icon={IconTrash}
                                    label={t('pdfEditor.pages.delete')}
                                    danger
                                    disabled={pages.length < 2}
                                    onClick={() => deletePages([page.uid])}
                                />
                            </div>
                        </div>
                    );
                })}

                <Hint label={t('pdfEditor.pages.insertAtEnd')}>
                    <button
                        type="button"
                        onClick={() => insertBlankPage(pages.length - 1)}
                        className="flex h-[34px] flex-none items-center justify-center gap-1.5 rounded-[9px] text-[12px] font-semibold text-[#8A827B] transition-colors hover:bg-[#F4F1ED] hover:text-[#3D3833] dark:text-zinc-400 dark:hover:bg-zinc-800"
                    >
                        <IconPlus size={14} stroke={2} />
                        {t('pdfEditor.pages.insertBlank')}
                    </button>
                </Hint>
            </div>
        </aside>
    );
}

function ThumbAction({ icon: Icon, label, onClick, danger, disabled }) {
    return (
        <Hint label={label}>
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                aria-label={label}
                className={`flex h-5 w-5 items-center justify-center rounded border border-[#ECE8E4] bg-white/95 shadow-sm disabled:opacity-40 dark:border-zinc-700 dark:bg-zinc-900/95 ${
                    danger ? 'text-[#D33A2C]' : 'text-[#3D3833] dark:text-zinc-200'
                }`}
            >
                <Icon size={12} stroke={1.9} />
            </button>
        </Hint>
    );
}
